import { MapPin, Phone, MessageCircle, Instagram, Clock, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";
import BookingForm from "@/components/BookingForm";
import { BUSINESS, WA_DEFAULT } from "@/data/business";

const CONTACT_ROWS = [
  {
    icon: MapPin,
    label: "Address",
    value: BUSINESS.address,
    action: { href: BUSINESS.mapsUrl, text: "Get Directions", testid: "visit-directions-button" },
  },
  {
    icon: Clock,
    label: "Opening Hours",
    value: "Daily — 10:00 AM to 8:00 PM",
  },
  {
    icon: Phone,
    label: "Phone",
    value: BUSINESS.phoneDisplay,
    action: { href: BUSINESS.phoneTel, text: "Call Now", testid: "visit-call-button" },
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: BUSINESS.whatsappDisplay,
    action: { href: WA_DEFAULT, text: "WhatsApp Now", testid: "visit-whatsapp-button", external: true },
  },
  {
    icon: Instagram,
    label: "Instagram",
    value: BUSINESS.instagramHandle,
    action: { href: BUSINESS.instagram, text: "Follow", testid: "visit-instagram-button", external: true },
  },
];

export default function Visit() {
  return (
    <main className="bg-ivory">
      <SEO
        title="Visit Us"
        description="Visit Glam Girl Makeup Studio at Patel Nagar Chauraha, in front of G.D. Goenika School, Hardattpur, Varanasi. Open daily 10 AM – 8 PM. Call, WhatsApp or request an appointment."
      />
      <section className="grain mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 lg:pt-40" data-testid="visit-section">
        <Reveal>
          <Eyebrow>Visit Us / Contact</Eyebrow>
          <h1 className="mt-4 max-w-2xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
            Visit Glam Girl <em className="text-rose">Makeup Studio.</em>
          </h1>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-2">
          <div className="space-y-4">
            {CONTACT_ROWS.map((row, i) => (
              <Reveal key={row.label} delay={0.05 * i}>
                <div
                  className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-charcoal/10 bg-white p-6"
                  data-testid={`visit-row-${row.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="flex min-w-0 items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream text-rose">
                      <row.icon className="h-4.5 w-4.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-charcoal/45">{row.label}</p>
                      <p className="mt-1 break-words text-sm font-medium leading-relaxed text-charcoal">{row.value}</p>
                    </div>
                  </div>
                  {row.action && (
                    <a
                      href={row.action.href}
                      {...(row.action.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      data-testid={row.action.testid}
                      className="group inline-flex shrink-0 items-center gap-1.5 rounded-full bg-charcoal px-5 py-2.5 text-xs font-semibold tracking-wide text-ivory transition-colors hover:bg-rose-deep"
                    >
                      {row.action.text}
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.28}>
              <div className="overflow-hidden rounded-2xl border border-charcoal/10 shadow-[0_24px_60px_rgba(27,25,22,0.1)]">
                <iframe
                  title="Glam Girl Makeup Studio on Google Maps"
                  src={BUSINESS.mapsEmbed}
                  className="aspect-[16/10] w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="visit-map-embed"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-charcoal/10 bg-white p-7 sm:p-9" data-testid="visit-booking-card">
              <h2 className="font-serif text-3xl font-medium text-charcoal">Request an appointment</h2>
              <p className="mt-2 text-sm leading-relaxed text-charcoal/55">
                Tell us what you need — we'll confirm on WhatsApp or a call.
              </p>
              <div className="mt-7">
                <BookingForm formId="visit-booking" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
